import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { jsPDF } from 'jspdf';
import * as PlotlyJS from 'plotly.js-dist-min';
import type { Layout } from 'plotly.js';
import { ExportGraphResult } from '../components/pages/assets/monitoring/graphs/dialogs/export-graph-dialog.component';
import { GraphDetail } from '../types/graph.interface';
import { downloadBlob } from '../utils/download.utils';
import { DEFAULT_STYLING } from './default-styling';
import { ToastService } from './toast.service';

interface ExportOptions {
  chartElement: HTMLElement;
  filename: string;
  options: ExportGraphResult;
  graph?: GraphDetail;
  originalLayout: Partial<Layout> | Record<string, unknown>;
}

type PdfOrientation = 'landscape' | 'portrait';

// Convert ``#RRGGBB`` (or rgba(...)) into an ``[r, g, b]`` triplet for jsPDF ``setTextColor``.
const colorToRgb = (color: string): [number, number, number] => {
  const trimmed = color.trim();
  if (trimmed.startsWith('#')) {
    const hex = trimmed.slice(1);
    const value = parseInt(hex.length === 3 ? hex.replace(/(.)/g, '$1$1') : hex.padEnd(6, '0').slice(0, 6), 16);
    return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
  }
  const match = trimmed.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (match?.[1] !== undefined && match[2] !== undefined && match[3] !== undefined) {
    return [parseInt(match[1], 10), parseInt(match[2], 10), parseInt(match[3], 10)];
  }
  return [0, 0, 0];
};

@Injectable({ providedIn: 'root' })
export class GraphExportService {
  private readonly document = inject(DOCUMENT);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslocoService);

  async executeExport(exportOpts: ExportOptions): Promise<void> {
    const { chartElement, filename, options, graph, originalLayout } = exportOpts;
    const dimensions = DEFAULT_STYLING.chart_dimensions;

    try {
      if (options.includeLegend) {
        const legend = DEFAULT_STYLING.legend_layout;
        const font = DEFAULT_STYLING.font;
        await PlotlyJS.relayout(chartElement, {
          showlegend: true,
          legend: {
            orientation: legend.orientation,
            yanchor: legend.yanchor,
            y: legend.y,
            xanchor: legend.xanchor,
            x: legend.x,
            font: { size: legend.font_size, family: font.family },
          },
          margin: { ...legend.margin },
        });
      } else {
        await PlotlyJS.relayout(chartElement, { showlegend: false });
      }

      if (options.action === 'clipboard') {
        await this.copyToClipboard(chartElement, options, graph);
        await PlotlyJS.relayout(chartElement, originalLayout);
        this.toastService.success(
          this.translate.translate('common.toasts.imageCopied'),
          false,
          this.translate.translate('common.toasts.copySuccess'),
        );
        return;
      }

      const headerText = options.headerText.trim();
      const hasHeader = !!headerText || (options.includeTitle && !!graph);

      if (options.format === 'pdf') {
        await this.exportToPdf(chartElement, filename, options, graph, headerText);
      } else if (hasHeader) {
        await this.exportToImageWithHeader(chartElement, filename, options, graph, headerText);
      } else {
        await PlotlyJS.downloadImage(chartElement, {
          format: options.format,
          filename,
          ...dimensions.standard,
        });
      }

      await PlotlyJS.relayout(chartElement, originalLayout);
      this.toastService.success(
        this.translate.translate('common.toasts.fileDownloaded', { filename }),
        false,
        this.translate.translate('common.toasts.exportSuccess'),
      );
    } catch (error) {
      await PlotlyJS.relayout(chartElement, originalLayout);
      this.toastService.error(
        this.translate.translate('common.toasts.exportError'),
        false,
        this.translate.translate('common.toasts.exportErrorTitle'),
      );
      throw error;
    }
  }

  private async exportToPdf(
    chartElement: HTMLElement,
    filename: string,
    options: ExportGraphResult,
    graph: GraphDetail | undefined,
    headerText: string,
  ): Promise<void> {
    const orientation: PdfOrientation = options.orientation ?? 'landscape';
    const dimensions = DEFAULT_STYLING.chart_dimensions;
    const { width: chartWidth, height: chartHeight } = orientation === 'portrait' ? dimensions.pdf_portrait : dimensions.standard;

    const dataUrl = await PlotlyJS.toImage(chartElement, { format: 'png', width: chartWidth, height: chartHeight });

    const pdf = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = margin;

    if (headerText || (options.includeTitle && graph)) {
      yPos = this.addPdfHeader(pdf, options.includeTitle, graph, margin, pageWidth, yPos, headerText);
    }

    const imgWidth = pageWidth - 2 * margin;
    const imgHeight = (chartHeight / chartWidth) * imgWidth;
    const availableHeight = pageHeight - yPos - margin;
    const finalHeight = Math.min(imgHeight, availableHeight);
    const finalWidth = (finalHeight / imgHeight) * imgWidth;

    pdf.addImage(dataUrl, 'PNG', margin, yPos, finalWidth, finalHeight);
    pdf.save(`${filename}.pdf`);
  }

  private addPdfHeader(
    pdf: jsPDF,
    includeTitle: boolean,
    graph: GraphDetail | undefined,
    margin: number,
    pageWidth: number,
    yPos: number,
    headerText: string,
  ): number {
    const centerX = pageWidth / 2;
    const theme = DEFAULT_STYLING.theme_colors;
    const secondary = colorToRgb(theme.text_secondary);
    const primary = colorToRgb(theme.text_primary);

    if (headerText) {
      pdf.setFontSize(10);
      pdf.setTextColor(...secondary);
      pdf.text(headerText, centerX, yPos + 4, { align: 'center' });
      yPos += 8;
    }

    if (includeTitle && graph) {
      pdf.setFontSize(18);
      pdf.setTextColor(...primary);
      pdf.text(graph.name, centerX, yPos + 6, { align: 'center' });
      yPos += 12;

      if (graph.description) {
        pdf.setFontSize(11);
        pdf.setTextColor(...secondary);
        const descLines: string[] = pdf.splitTextToSize(graph.description, pageWidth - 2 * margin) as string[];
        pdf.text(descLines, centerX, yPos + 4, { align: 'center' });
        yPos += descLines.length * 5 + 4;
      }
    }

    return yPos;
  }

  private async exportToImageWithHeader(
    chartElement: HTMLElement,
    filename: string,
    options: ExportGraphResult,
    graph: GraphDetail | undefined,
    headerText: string,
  ): Promise<void> {
    const { width: chartWidth, height: chartHeight } = DEFAULT_STYLING.chart_dimensions.standard;
    const headerHeight = this.calculateHeaderHeight(options.includeTitle, graph, headerText);
    const totalHeight = headerHeight + chartHeight;

    if (options.format === 'svg') {
      await this.exportToSvgWithHeader(
        chartElement,
        filename,
        options.includeTitle,
        graph,
        chartWidth,
        chartHeight,
        headerHeight,
        headerText,
      );
    } else {
      await this.exportToPngWithHeader(
        chartElement,
        filename,
        options.includeTitle,
        graph,
        chartWidth,
        chartHeight,
        headerHeight,
        totalHeight,
        headerText,
      );
    }
  }

  private calculateHeaderHeight(includeTitle: boolean, graph: GraphDetail | undefined, headerText: string): number {
    if (!headerText && !(includeTitle && graph)) return 0;
    const h = DEFAULT_STYLING.header_layout;
    const customHeaderHeight = headerText ? h.custom_header_height : 0;
    const titleHeight = includeTitle && graph ? h.title_height : 0;
    const descHeight = includeTitle && graph?.description ? h.desc_height + h.desc_spacing : 0;
    return h.padding * 2 + customHeaderHeight + titleHeight + descHeight;
  }

  private async exportToPngWithHeader(
    chartElement: HTMLElement,
    filename: string,
    includeTitle: boolean,
    graph: GraphDetail | undefined,
    chartWidth: number,
    chartHeight: number,
    headerHeight: number,
    totalHeight: number,
    headerText: string,
  ): Promise<void> {
    const chartDataUrl = await PlotlyJS.toImage(chartElement, { format: 'png', width: chartWidth, height: chartHeight });
    const theme = DEFAULT_STYLING.theme_colors;

    const canvas = this.document.createElement('canvas');
    canvas.width = chartWidth;
    canvas.height = totalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = theme.background;
    ctx.fillRect(0, 0, chartWidth, totalHeight);

    this.drawCanvasHeader(ctx, includeTitle, graph, chartWidth, headerText);

    const chartImage = new Image();
    chartImage.src = chartDataUrl;
    await new Promise<void>((resolve, reject) => {
      chartImage.onload = () => {
        ctx.drawImage(chartImage, 0, headerHeight);
        resolve();
      };
      chartImage.onerror = reject;
    });

    const link = this.document.createElement('a');
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();

    canvas.width = 0;
    canvas.height = 0;
    chartImage.src = '';
  }

  private async exportToSvgWithHeader(
    chartElement: HTMLElement,
    filename: string,
    includeTitle: boolean,
    graph: GraphDetail | undefined,
    chartWidth: number,
    chartHeight: number,
    headerHeight: number,
    headerText: string,
  ): Promise<void> {
    const chartSvg = await PlotlyJS.toImage(chartElement, { format: 'svg', width: chartWidth, height: chartHeight });
    const chartSvgContent = await fetch(chartSvg).then(r => r.text());

    const theme = DEFAULT_STYLING.theme_colors;
    const font = DEFAULT_STYLING.font;
    const layout = DEFAULT_STYLING.header_layout;
    const bandOffsets = DEFAULT_STYLING.svg_band_offsets;
    const centerX = chartWidth / 2;
    let yPos = 0;

    const headerTextElement = headerText
      ? `<text x="${centerX}" y="${(yPos += bandOffsets.header_baseline)}" text-anchor="middle" fill="${theme.text_secondary}" font-family="${font.family}" font-size="${layout.desc_font_size}">${this.escapeXml(headerText)}</text>`
      : '';

    const titleElement =
      includeTitle && graph
        ? `<text x="${centerX}" y="${(yPos += bandOffsets.title_advance)}" text-anchor="middle" fill="${theme.text_primary}" font-family="${font.family}" font-size="${layout.title_font_size}" font-weight="bold">${this.escapeXml(graph.name)}</text>`
        : '';

    const descriptionElement =
      includeTitle && graph?.description
        ? `<text x="${centerX}" y="${(yPos += bandOffsets.description_advance)}" text-anchor="middle" fill="${theme.text_secondary}" font-family="${font.family}" font-size="${layout.desc_font_size}">${this.escapeXml(graph.description)}</text>`
        : '';

    const wrappedSvg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${chartWidth}" height="${headerHeight + chartHeight}" viewBox="0 0 ${chartWidth} ${headerHeight + chartHeight}">
  <rect width="100%" height="100%" fill="${theme.background}"/>
  ${headerTextElement}
  ${titleElement}
  ${descriptionElement}
  <g transform="translate(0, ${headerHeight})">
    ${chartSvgContent
      .replace(/<\?xml[^?]*\?>/, '')
      .replace(/<svg[^>]*>/, '')
      .replace(/<\/svg>/, '')}
  </g>
</svg>`;

    downloadBlob(new Blob([wrappedSvg], { type: 'image/svg+xml' }), `${filename}.svg`);
  }

  private escapeXml(text: string): string {
    const replacements: Record<string, string> = { '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;' };
    return text.replace(/[<>&'"]/g, c => replacements[c] ?? c);
  }

  private async copyToClipboard(chartElement: HTMLElement, options: ExportGraphResult, graph?: GraphDetail): Promise<void> {
    const dimensions = DEFAULT_STYLING.chart_dimensions;
    const theme = DEFAULT_STYLING.theme_colors;
    const { width: chartWidth, height: chartHeight } = dimensions.standard;
    const headerText = options.headerText.trim();
    const headerHeight = this.calculateHeaderHeight(options.includeTitle, graph, headerText);
    const totalHeight = headerHeight + chartHeight;

    const chartDataUrl = await PlotlyJS.toImage(chartElement, { format: 'png', width: chartWidth, height: chartHeight });

    const canvas = this.document.createElement('canvas');
    canvas.width = chartWidth;
    canvas.height = totalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context unavailable');

    ctx.fillStyle = theme.background;
    ctx.fillRect(0, 0, chartWidth, totalHeight);

    if (headerHeight > 0) {
      this.drawCanvasHeader(ctx, options.includeTitle, graph, chartWidth, headerText);
    }

    const chartImage = new Image();
    chartImage.src = chartDataUrl;
    await new Promise<void>((resolve, reject) => {
      chartImage.onload = () => {
        ctx.drawImage(chartImage, 0, headerHeight);
        resolve();
      };
      chartImage.onerror = reject;
    });

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Blob creation failed'))), 'image/png');
    });

    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);

    canvas.width = 0;
    canvas.height = 0;
    chartImage.src = '';
  }

  private drawCanvasHeader(
    ctx: CanvasRenderingContext2D,
    includeTitle: boolean,
    graph: GraphDetail | undefined,
    canvasWidth: number,
    headerText: string,
  ): void {
    const h = DEFAULT_STYLING.header_layout;
    const theme = DEFAULT_STYLING.theme_colors;
    const fontFamily = DEFAULT_STYLING.font.family;
    const centerX = canvasWidth / 2;
    let yOffset = h.padding;

    ctx.textAlign = 'center';

    if (headerText) {
      ctx.fillStyle = theme.text_secondary;
      ctx.font = `${h.desc_font_size}px ${fontFamily}`;
      ctx.fillText(headerText, centerX, yOffset + h.desc_font_size);
      yOffset += h.custom_header_height;
    }

    if (includeTitle && graph) {
      ctx.fillStyle = theme.text_primary;
      ctx.font = `bold ${h.title_font_size}px ${fontFamily}`;
      ctx.fillText(graph.name, centerX, yOffset + h.title_font_size);
      yOffset += h.title_height + h.desc_spacing;

      if (graph.description) {
        ctx.fillStyle = theme.text_secondary;
        ctx.font = `${h.desc_font_size}px ${fontFamily}`;
        ctx.fillText(graph.description, centerX, yOffset + h.desc_font_size);
      }
    }

    ctx.textAlign = 'start';
  }
}
